import { RiCloseLine, RiQuestionLine } from "@remixicon/react";
import classNames from "classnames";
import React, {
    FormEvent, ReactElement, ReactNode, useEffect, useRef, useState,
} from "react";
import { Modal } from "react-bootstrap";
import useKeyboardShortcuts, { useKeyboardState } from "../../contexts/KeyboardShortcutContext";
import { LoggingHandler } from "../../contexts/logging/LoggingContext";
import { useTheme } from "../../contexts/Theme";
import Help from "../help/Help";
import Button, { ButtonProps } from "../theme/button/Button";
import "./Confirm.scss";

interface Props {
    confirmText?: string;
    closeText?: string;
    closeVariant?: ButtonProps["variant"];
    children: ReactNode;
    size?: "sm" | "lg" | "xl";
    shouldShow: boolean;
    onHide?: () => void;
    onConfirm?: (event?: FormEvent) => (void | Promise<void> | unknown);
    className?: string;
    bodyClassName?: string;
    dialogClassName?: string;
    rootClassName?: string;
    keyboardContext?: string;
    confirmActionText?: string;
    confirmVariant?: string;
    shouldCenter?: boolean;
    shouldShowFooter?: boolean;
    fullscreen?: string;
    shouldDisableEscKey?: boolean;
    shouldEnforceFocus?: boolean;
    title?: ReactNode;
    helpArticleId?: string;
    helpText?: string;
    isDefaultBackground?: boolean;
    confirmDisabledReason?: string
    confirmMetricPath?: string[];
    hideMetricPath?: string[];
    footerButtons?: ReactNode;
    shouldShowFooterCloseButton?: boolean;
    onEntered?: () => void;
}

const KEYBOARD_STATE = "confirm";
export default function Confirm(props: Props): ReactElement | null {
    const ref = React.createRef<HTMLFormElement>();
    const keyboardState = useKeyboardState();
    const [confirmActionText, setConfirmActionText] = useState("");
    const submitRef = useRef<HTMLButtonElement>(null);

    const theme = useTheme();

    // const ref = useMemo(() => React.createRef<HTMLFormElement>(), []);
    // const showValue = useMemo(() => ({
    //     shouldShow: props.shouldShow,
    // }), [props.shouldShow]);
    // showValue.shouldShow = props.shouldShow;

    const onHide = props.onHide && props.hideMetricPath
        ? LoggingHandler.instance.wrapMetric(props.hideMetricPath, props.onHide)
        : props.onHide;

    useKeyboardShortcuts(shortcuts => {
        shortcuts.on({
            shortcut: "cmd+enter",
            context: props.keyboardContext ?? KEYBOARD_STATE,
            shouldUseAllContexts: true,
            handler: () => {
                if (!props.shouldShow) {
                    return;
                }
                if (ref.current?.checkValidity()) {
                    props.onConfirm?.();
                } else {
                    ref.current?.reportValidity();
                }
            },
        });
        shortcuts.on({
            shortcut: "esc",
            shouldUseAllContexts: true,
            context: props.keyboardContext ?? KEYBOARD_STATE,
            handler: () => {
                if (!props.shouldShow) {
                    return;
                }
                if (props.shouldDisableEscKey) {
                    return;
                }
                keyboardState.popContext(props.keyboardContext ?? KEYBOARD_STATE);
                onHide?.();
            },
        });
    });

    useEffect(() => {
        if (submitRef?.current && !props.confirmDisabledReason) {
            submitRef.current.disabled = false;
        }
        if (props.shouldShow) {
            keyboardState.pushContext(props.keyboardContext ?? KEYBOARD_STATE);
        } else {
            keyboardState.popContext(props.keyboardContext ?? KEYBOARD_STATE);
        }
    }, [props.shouldShow, props.confirmDisabledReason]);
    const submit = (event: FormEvent) => {
        const isDisabled = submitRef.current?.disabled;

        if (submitRef?.current) {
            submitRef.current.disabled = true;
        }

        event.preventDefault();
        event.stopPropagation();
        if (isDisabled) {
            return false;
        }
        LoggingHandler.instance.nextTick(async () => {
            try {
                if (props.confirmMetricPath) {
                    LoggingHandler.instance.metric(props.confirmMetricPath);
                }
                await props.onConfirm?.(event);
            } finally {
                if (submitRef?.current) {
                    submitRef.current.disabled = false;
                }
            }
        });

        return false;
    };
    if (!props.shouldShow) {
        return null;
    }
    const shouldShowFooter = props.shouldShowFooter != null
        ? props.shouldShowFooter
        : (onHide || (props.confirmText && (!props.confirmActionText || props.confirmActionText === confirmActionText)));
    return (
        <Modal
            show={props.shouldShow}
            // onHide={() => {
            //     keyboardState.popContext(props.keyboardContext ?? KEYBOARD_STATE);
            // }}
            // onHide={props.onHide}
            className={classNames("confirm-modal", props.rootClassName)}
            size={props.size}
            contentClassName={classNames(
                props.className,
                "h-100 shadow",
                props.isDefaultBackground ? "modal-default-background" : undefined,
            )}
            dialogClassName={props.dialogClassName}
            centered={props.shouldCenter}
            fullscreen={props.fullscreen}
            enforceFocus={props.shouldEnforceFocus}
            onEntered={props.onEntered}
        >
            <form
                onSubmit={props.confirmText ? submit : undefined}
                ref={ref}
                className="d-flex flex-column h-100 modal-max-height"
            >
                {props.title && (
                    <Modal.Header className="d-flex">
                        <h4 className={theme.isV3 ? "flex-grow-1" : "m-0"}>{props.title}</h4>
                        {props.helpArticleId && (
                            <Help
                                articleId={props.helpArticleId}
                                buttonClassName="btn-none btn me-4"
                                button={(
                                    <div className="d-flex align-items-center gap-1 modal-help-link">
                                        <RiQuestionLine />
                                        {props.helpText ?? "Help"}
                                    </div>
                                )}
                                buttonClicked={() => {
                                    // props.onHide?.();
                                }}
                            />
                        )}
                        {onHide && (
                            <Button
                                prefixIcon={<RiCloseLine />}
                                variant="none"
                                size="small"
                                className={theme.isV3 ? "" : "p-0"}
                                onClick={onHide}
                            />
                        )}
                    </Modal.Header>
                )}
                <Modal.Body className={`overflow-auto flex-grow-1 flex-shrink-1 ${props.bodyClassName}`}>
                    {props.children}

                    {props.confirmActionText && (
                        <div>
                            <small>
                                Please type
                                {" "}
                                <code>{props.confirmActionText}</code>
                                {" "}
                                in the box below to continue.
                            </small>
                            <input
                                type="text"
                                className="form-control"
                                placeholder={props.confirmActionText}
                                onChange={event => setConfirmActionText(event.target.value)}
                            />
                        </div>
                    )}
                </Modal.Body>

                {shouldShowFooter && (
                    <Modal.Footer className="flex-grow-0">
                        {onHide && props.shouldShowFooterCloseButton && (
                            <Button
                                variant={props.closeVariant as ButtonProps["variant"]
                                    ?? (theme.isV3 ? "outline-gray" : "secondary")}
                                onClick={onHide}
                            >
                                {props.closeText}
                            </Button>
                        )}
                        {props.footerButtons}

                        {(props.confirmText && (!props.confirmActionText || props.confirmActionText === confirmActionText)) && (
                            <Button
                                type="submit"
                                variant={props.confirmVariant as ButtonProps["variant"]}
                                ref={submitRef}
                                disabled={props.confirmDisabledReason != null}
                                title={props.confirmDisabledReason}
                            >
                                {props.confirmText}
                            </Button>
                        )}

                    </Modal.Footer>
                )}

            </form>

        </Modal>
    );
}

Confirm.defaultProps = {
    closeText: "Close",
    confirmText: undefined,
    onConfirm: undefined,
    className: "",
    dialogClassName: "",
    bodyClassName: "",
    rootClassName: "",
    onHide: undefined,
    size: "",
    keyboardContext: undefined,
    confirmActionText: undefined,
    confirmVariant: "primary",
    shouldCenter: undefined,
    fullscreen: undefined,
    shouldDisableEscKey: undefined,
    shouldEnforceFocus: true,
    shouldShowFooter: undefined,
    closeVariant: undefined,
    title: undefined,

    helpArticleId: undefined,
    helpText: undefined,
    isDefaultBackground: false,
    confirmDisabledReason: undefined,
    confirmMetricPath: undefined,
    hideMetricPath: undefined,
    footerButtons: undefined,
    shouldShowFooterCloseButton: true,
    onEntered: undefined,
};

type ConfirmProps = Omit<Props, "shouldShow" | "children"> & {
    body: ReactNode;
}
export function useConfirm(props: ConfirmProps) {
    const [shouldShow, setShouldShow] = useState(false);

    const {
        body,
        ...rest
    } = props;

    return {
        setShouldShow,
        element: (
            <Confirm
                {...rest}
                onHide={() => {
                    setShouldShow(false);
                    props.onHide?.();
                }}
                onConfirm={event => {
                    setShouldShow(false);
                    props.onConfirm?.(event);
                }}
                shouldShow={shouldShow}
            >
                {body}
            </Confirm>
        ),
    };
}
